import React, { useEffect, useState } from "react";
import { ISearchContext, useSearch } from "../SearchContext";
import { IListingSummary } from "../../../lib/Listing";
import { BiReset, BiCalendar } from "react-icons/bi";
import Select from "react-select";
import Checkbox from "../../Forms/Checkbox";
import RadioButton from "../../Forms/RadioButton";

const range = (start: number, end: number) => {
  const length = end - start;
  return Array.from({ length }, (_, i) => start + i);
};

export interface IFilterProps {
  /*
   * Label text.
   */
  label: string;
  /**
   * The query parameter/search filters key for the filter.
   */
  param: string;
  /**
   * The default or current value or values if any for the filter.
   */
  value?: any;
  /**
   * Unit of measurement for value.
   */
  unit?: string;
  /**
   * Placeholder text, where supported.
   */
  placeholder?: string;
  /**
   * Help text or instructions for the filter, eg. tooltip
   */
  help?: string;
  /**
   * Additional class name(s) to apply to the filter container element.
   */
  className?: string;
}

export interface ICountMinProps extends IFilterProps {
  range: [min: number, max: number];
  countSource: keyof IListingSummary;
  reactSelectStyles?: {};
}

export const CountMin: React.FC<ICountMinProps> = (props) => {
  const context = useSearch();

  // Call setFilterValue from the context onChange
  const onChange = (option: any) => {
    if (option.value) {
      context.setFilterValue &&
        context.setFilterValue(props.param, option.value.toString());
    } else {
      context.setFilterValue && context.setFilterValue(props.param, null);
    }
  };
  let value = context.getFilterValue && context.getFilterValue(props.param);
  useEffect(() => {
    // Register this filter in the search context if necessary.
    context.currentFilter &&
      !context.currentFilter.get(props.param) &&
      context.currentFilter.set(props.param, (arg, value) => {
        let item = arg as IListingSummary;
        return (item[props.countSource] || 0) >= parseInt(value);
      });
  }, [context.currentFilter]);
  let opts = range(props.range[0], props.range[1]).map((num, index) => {
    return { value: num, label: num + "+" };
  });
  let defaultValue: any = opts.find((opt) => opt.value.toString() == value);
  if (defaultValue === undefined) defaultValue = null;
  return (
    <div className={props.className}>
      <label htmlFor={props.param}>
        <span className="sr-only">{props.label}</span>
        <Select
          name={props.param}
          value={defaultValue}
          className="max-w-full"
          onChange={onChange}
          placeholder={props.placeholder}
          options={opts}
          styles={props.reactSelectStyles}
        />
      </label>
    </div>
  );
};

export const Reset: React.FC<{ text: string; className?: string }> = (
  props
) => {
  const context = useSearch();
  if (!Object.keys(context.currentFilterValues || {}).length) return <></>;
  return (
    <div className={props.className}>
      <a
        onClick={(event) =>
          context.resetFilterValues && context.resetFilterValues()
        }
        className="cursor-pointer"
      >
        <BiReset className="text-xl mr-1.5 inline" />
        {props.text}
      </a>
    </div>
  );
};

export interface IChooseProps extends IFilterProps {
  /**
   * The resolver function takes an item from the index and
   * returns the item's value relevant to this item, for use
   * in populating available options and in default filter
   * behavior.
   */
  resolver: (arg: any) => string | [string];
  /**
   * Sorting order for options in the calculated options list.  Ignored if options
   * parameter is passed. "alpha"betical or by "count" of matches
   */
  sort?: "alpha" | "count";
  /**
   * Whether the default filter behavior should be to match "one" single selected option,
   * "any" of the mutliple selected options, or "all" of the multiple selected options
   */
  match?: "one" | "any" | "all";
  /**
   * Styles to pass to the Select component
   */
  reactSelectStyles?: {};
  /**
   * Default behavior of this filter is to match input selections against
   * the value(s) on the index item, but that can be overidden for logic
   * such as greater than/less than, fuzzy search, etc.
   */
  customFilter?: (arg: any, value: string | [string]) => boolean;

  /**
   * Custom options for the select list.  If this parameter is not provided options
   * will be calculated by applying the resolver to all items in the search context
   * index.
   */
  options?: {
    value: string;
    label: string;
  }[];

  /**
   * Add an "All" option to the options list.
   */
  showAllEntriesOption?: boolean;
  /**
   * "All" option text.
   */
  allEntriesOptionText?: string;

  context: ISearchContext;
  onChange?: (option: ISelectOptionType) => void;
  onSetInitialValue?: (value: any) => void;
  selectedValue?: any;
}

type ISelectOptionType =
  | {
    value: string;
    label: string;
  }
  | [
    {
      value: string;
      label: string;
    }
  ];

export const Choose: React.FC<IChooseProps> = (props) => {
  const [isClient, setClient] = useState(false);
  const key = isClient ? "client" : "server";

  const context = props.context;
  const [map, setMap] = useState<Map<string, number> | null>(null);
  const choose = props.match || "one";
  const isMulti = choose == "one" ? false : true;
  const showAllOption = props.showAllEntriesOption ?? false;
  const allOptionText = props.allEntriesOptionText ?? "All";
  let opts = props.options || [];
  if (!props.options) {
    // Build our options from the index
    if (map == null) {
      let newopts = new Map<string, number>();
      // Determine options/choices and number of matches from available index data.
      context.index &&
        context.index.map((arg) => {
          let val = props.resolver(arg);
          if (Array.isArray(val)) {
            val.forEach((v) => newopts.set(v, (newopts.get(v) || 0) + 1));
          } else {
            val && newopts.set(val, (newopts.get(val) || 0) + 1);
          }
        });

      setMap(newopts);
    } else {
      map.forEach((value, key) => {
        opts.push({ value: key, label: key });
      });
      opts.sort((a: any, b: any) => {
        switch (props.sort) {
          case "count":
            return (map.get(b.value) || 0) - (map.get(a.value) || 0);
            break;
          default:
            return a.value.localeCompare(b.value);
            break;
        }
      });
    }
  }
  //Add the "All" option
  showAllOption && opts.splice(0, 0, { label: allOptionText, value: "All" })
  const defaultFilter = (arg: any, value: string | [string]) => {
    let varr = (value instanceof Array ? value : value ? [value] : []) as [
      string
    ];
    let vstr = (value instanceof Array ? value[0] : value) as string;
    const resolve = props.resolver(arg);

    if (showAllOption && (vstr == "All" || varr.includes("All"))) {
      return true;
    }

    switch (choose) {
      case "any":
        return varr.find(
          (str) =>
            (Array.isArray(resolve) && resolve.find((subv) => subv == str)) ||
            (typeof resolve === "string" && resolve == str)
        );
        break;
      case "all":
        return varr.find(
          (str) =>
            (Array.isArray(resolve) &&
              resolve.filter((x) => varr.includes(x)).length == varr.length) ||
            (typeof resolve === "string" && resolve == str)
        );
        break;
      default:
        return (
          (Array.isArray(resolve) &&
            resolve.find((str) => vstr == str) !== undefined) ||
          (typeof resolve == "string" && resolve == vstr)
        );
        break;
    }
  };
  useEffect(() => {
    // Register this filter in the search context if necessary.
    context.currentFilter &&
      !context.currentFilter.get(props.param) &&
      context.setFilter &&
      context.setFilter(props.param, props.customFilter || defaultFilter);
  }, [context.currentFilter]);
  // Call setFilterValue from the context onChange
  const onChange = (option: ISelectOptionType) => {
    if (isMulti) {
      // Option should be an array
      if (Array.isArray(option) && option.length > 0) {
        context.setFilterValue &&
          context.setFilterValue(
            props.param,
            option.map((opt) => {
              return opt.value;
            })
          );
      } else {
        context.setFilterValue && context.setFilterValue(props.param, null);
      }
    } else {
      // Option should be {value: string, label: string}
      if (!Array.isArray(option) && option.value.length) {
        context.setFilterValue &&
          context.setFilterValue(props.param, option.value);
      } else {
        context.setFilterValue && context.setFilterValue(props.param, null);
      }
    }

    props.onChange && props.onChange(option)
  };
  let value: string | string[] | undefined = ""
  if (context.getFilterValue && context.getFilterValue(props.param) !== undefined) {
    value = context.getFilterValue(props.param)
    props.onSetInitialValue && props.onSetInitialValue(value)
  }

  let defaultValue: any = isMulti
    ? opts.filter(
      (opt) =>
        (Array.isArray(value) && value.find((val) => opt.value == val)) ||
        value == opt.value
    )
    : opts.find((opt) => opt.value == value);
  if (defaultValue === undefined) defaultValue = null;

  useEffect(() => {
    setClient(true);
  }, []);

  return (
    <div className={props.className} key={key}>
      <label htmlFor={props.param}>
        <span className="sr-only">{props.label}</span>
        <Select
          name={props.param}
          value={props.selectedValue ?? defaultValue}
          placeholder={props.placeholder}
          className="max-w-full"
          options={opts}
          onChange={onChange}
          isMulti={isMulti}
          styles={props.reactSelectStyles}
        />
      </label>
    </div>
  );
};

export interface ITextProps extends IFilterProps {
  /*
   * Given a search result returns true iff the item matches text.
   */
  resolver: (arg: any, value: string) => boolean;
  /*
   *  Classes to be placed on the input
   */
  inputClassName?: string;
  /**
   * Function to call on click of the submit arrow
   */
  onSubmit?: (filters: {}) => void;
  context?: ISearchContext;
}

export const Text: React.FC<ITextProps> = (props) => {
  let context = props.context ? props.context : useSearch();

  useEffect(() => {
    // Register this filter in the search context if necessary.
    context.currentFilter &&
      !context.currentFilter.get(props.param) &&
      context.setFilter &&
      context.setFilter(props.param, (arg, value) => {
        if (value.length < 2) return true;
        return props.resolver(arg, value);
      });
  }, [context.currentFilter]);

  const onChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    if (event.target.value) {
      context.setFilterValue &&
        context.setFilterValue(props.param, event.target.value);
    } else {
      context.setFilterValue && context.setFilterValue(props.param, null);
    }
  };

  let value = context.getFilterValue && context.getFilterValue(props.param);
  return (
    <div className={props.className}>
      <label htmlFor={props.param}>
        <span className="sr-only">{props.label}</span>
        <input
          type="text"
          className={`px-2.5 py-1.5 border-b ${props.inputClassName}`}
          placeholder={props.placeholder}
          onChange={onChange}
          onKeyPress={(event) => {
            if (event.key === 'Enter') {
              props.onSubmit && props.onSubmit(context.currentFilterValues || {})
            }
          }}
          value={value == undefined ? "" : value}
        />
      </label>
    </div>
  );
};

export interface ICheckProps extends IFilterProps {
  /*
   * Given a search result returns true iff the item matches text.
   */
  resolver: (arg: any, value?: any) => boolean;
  /*
   *  Classes to be placed on the input
   */
  inputClassName?: string;
  context: ISearchContext;
  /**
   * Whether the default filter behavior should be to match "any" of the mutliple selected options, 
   * or "all" of the multiple selected options
   */
  match?: "any" | "all";
  inputValue?: string;
  idName?: string;
}

export const Check: React.FC<ICheckProps> = (props) => {
  let context = props.context;
  const choose: string = props.match || "all";
  useEffect(() => {
    // Register this filter in the search context if necessary.
    context.currentFilter &&
      !context.currentFilter.get(props.param) &&
      context.setFilter &&
      context.setFilter(props.param, (arg, value) => {
        return props.resolver(arg, value);
      });
  }, [context.currentFilter]);

  const onChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    if (context.setFilterValue) {
      if (event.target.checked) {
        if (choose == "all") {
          context.setFilterValue(props.param, event.target.value);
        }
        else {
          const currentFilter = Object.assign([], (context.getFilterValue!(props.param) || []) as string[]);
          currentFilter.push(event.target.value);
          context.setFilterValue(props.param, currentFilter);
        }
      }
      else {
        if (choose == "all") {
          context.setFilterValue(props.param, null);
        }
        else {
          const currentFilter = Object.assign([], (context.getFilterValue!(props.param) || []) as string[]);
          const index = currentFilter?.indexOf(event.target.value)
          if (index > -1) {
            currentFilter.splice(index, 1)
            if (currentFilter.length > 0) {
              context.setFilterValue(props.param, currentFilter);
            }
            else {
              context.setFilterValue(props.param, null);
            }
          }
        }
      }
    }
  };

  const isChecked = (value: string | string[] | undefined) => {
    if (choose == "all") {
      return value == (props.inputValue ?? "on")
    }
    else {
      return ((value ?? []) as string[]).includes(props.inputValue ?? "on")
    }
  };

  let inputProps: Partial<React.InputHTMLAttributes<HTMLInputElement>> = {};
  props.inputValue && (inputProps = { value: props.inputValue });

  let value = context.getFilterValue && context.getFilterValue(props.param);
  return (
    <div className={props.className}>
      <Checkbox
        idName={props.idName ?? props.param}
        label={props.label}
        inputClassName={props.inputClassName}
        inputProps={{
          onChange: onChange,
          checked: isChecked(value),
          ...inputProps
        }}
      />
    </div>
  );

};

export interface IRadioProps extends IFilterProps {
  /*
   * Given a search result returns true iff the item matches text.
   */
  resolver: (arg: any, value?: any) => boolean;
  /*
   *  Classes to be placed on the input
   */
  inputClassName?: string;
  context: ISearchContext;
  id: string;
  name: string;
  value: string;
}

export const Radio: React.FC<IRadioProps> = (props) => {
  let context = props.context;

  useEffect(() => {
    // Register this filter in the search context if necessary.
    context.currentFilter &&
      !context.currentFilter.get(props.param) &&
      context.setFilter &&
      context.setFilter(props.param, (arg, value) => {
        return props.resolver(arg, value);
      });
  }, [context.currentFilter]);

  const onChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    if (event.target.checked) {
      context.setFilterValue &&
        context.setFilterValue(props.param, event.target.value);
    } else {
      context.setFilterValue && context.setFilterValue(props.param, null);
    }
  };

  let value = context.getFilterValue && context.getFilterValue(props.param);
  return (
    <div className={props.className}>
      <RadioButton
        id={props.id}
        name={props.name}
        label={props.label}
        inputClassName={props.inputClassName}
        inputProps={{
          onChange: onChange,
          checked: value == props.value,
          value: props.value
        }}
      />
    </div>
  );
}
